import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import
{
  Button,
  Icon,
  Container,
  Text,
  Input,
  Checkbox,
  Slider,
  FormAlert,
  UpdatePasswordDialog,
  UpdateProfileDialog,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import { useAuth } from '../contexts/AuthContext';
import { getRedirectToFromQuery, getDefaultRedirectPath } from '../utils/redirectUtils';
import { isAdminUser } from '../admin/utils';
import { isValidEmail } from '../utils/validators';
import { useTurnstile } from '../hooks/useTurnstile';
import { SIGNUP_ENABLED } from '../config/features';

interface ActionData {
  action: string;
  user_email?: string;
  next_step?: string;
  show_popup?: boolean;
  change_endpoint?: string;
  profile_endpoint?: string;
  update_endpoint?: string;
}

interface LoginError {
  detail: string;
  action?: string;
  action_data?: ActionData;
}

export const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  const [showUpdateProfileModal, setShowUpdateProfileModal] = useState(false);
  const [actionData, setActionData] = useState<ActionData | null>(null);
  
  const [searchParams] = useSearchParams();
  const { login, activeRole, user } = useAuth();
  const navigate = useNavigate();
  const { turnstileRef, token: turnstileToken, isReady: turnstileReady, isAvailable: turnstileAvailable, resetTurnstile } = useTurnstile({ action: 'login' });

  // Get email error message
  const getEmailError = (): string => {
    if (!emailTouched) return '';
    if (!username) return 'Email is required';
    if (!isValidEmail(username)) return 'Please enter a valid email address';
    return '';
  };

  // Get password error message
  const getPasswordError = (): string => {
    if (!passwordTouched) return '';
    if (!password) return 'Password is required';
    return '';
  };

  // Check if form has any errors or empty fields
  const hasFormErrors = (): boolean => {
    return !username || !password || !isValidEmail(username);
  };

  useEffect(() => {
    // Check for session expiry message on component mount
    const sessionError = sessionStorage.getItem('loginError');
    if (sessionError) {
      setError(sessionError);
      // Clear the session error after displaying it
      sessionStorage.removeItem('loginError');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setActionData(null);

    try {
      const loggedInUser = await login(username, password, turnstileToken || undefined);
      
      // Get redirectTo from URL query params with validation
      // Use loggedInUser from login response instead of context user (which might not be updated yet)
      const userHasAdminRole = isAdminUser(loggedInUser);
      const redirectTo = getRedirectToFromQuery(searchParams, userHasAdminRole);
      
      // Navigate to redirectTo path or default based on role
      if (redirectTo) {
        // Redirect to the validated path from query parameter
        navigate(redirectTo, { replace: true });
      } else {
        // No redirectTo param or invalid path - go to default based on role
        // Use loggedInUser to determine default path
        const defaultPath = getDefaultRedirectPath(activeRole, userHasAdminRole);
        navigate(defaultPath, { replace: true });
      }
    } catch (error: any) {
      console.log('🔍 Login error details:', error.response?.data);
      
      if (error.response?.status === 403 && error.response?.data?.action) {
        const errorData: LoginError = error.response.data;
        
        setError(errorData.detail);
        setActionData(errorData.action_data || null);
        
        // Show appropriate modal based on action
        if (errorData.action === 'UPDATE_PASSWORD' && errorData.action_data?.show_popup) {
          setShowUpdatePasswordModal(true);
        } else if (errorData.action === 'UPDATE_PROFILE' && errorData.action_data?.show_popup) {
          setShowUpdateProfileModal(true);
        }
      } else {
        let errMsg = 'Login failed. Please try again.';
        
        if (error.response?.data?.detail) {
          errMsg = error.response.data.detail;
        } else if (error.message) {
          errMsg = error.message;
        }
        
        setError(errMsg);
      }
    } finally {
      setIsLoading(false);
      resetTurnstile();
    }
  };

  const handlePasswordUpdateSuccess = () => {
    setShowUpdatePasswordModal(false);
    setActionData(null);
    setError('');
    setPassword(''); // Clear password field so user enters new one
    // Show success message and prompt to login again
    setSuccessMessage('Password updated successfully! Please login with your new password.');
  };

  const handleProfileUpdateSuccess = () => {
    setShowUpdateProfileModal(false);
    setActionData(null);
    setError('');
    setPassword(''); // Clear password field so user enters new one if needed
    // Show success message and prompt to login again
    setSuccessMessage('Profile updated successfully! Please login again.');
  };

  const handleCloseModals = () => {
    setShowUpdatePasswordModal(false);
    setShowUpdateProfileModal(false);
    setActionData(null);
  };

  const handleDismissError = () => {
    setError('');
    setActionData(null);
    sessionStorage.removeItem('loginError');
  };

  const handleDismissSuccess = () => {
    setSuccessMessage('');
  };

  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block col md:sticky md:top-0">
        <Container padding={"4xl"} className="min-h-screen flex items-center justify-center bg-bg-secondary-alt">
          <Slider  autoPlayInterval={5000} />
        </Container>
      </div>
      <div className="col">
        <div className="bg-bg-secondary-alt">
          <Container padding={"xl"} className="min-h-screen flex items-center justify-center bg-bg-tertiary">
            <Container padding={"4xl"} className="w-full max-w-md flex flex-col gap-bg-v2-xl rounded-bg-v2-md container-shadow-lg hover-scale-sm page-transition-enter">
                <div className="flex items-center justify-center gap-bg-v2-md animate-initial-hidden animate-fade-in-down animate-delay-200">
                    <Icon name={IconName.BrandLogo} />
                    <span
                      className="text-text-brand-primary font-semibold"
                      style={{
                        fontFamily: 'Inter',
                        fontSize: 'var(--font-size-display-sm, 30px)',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'var(--line-height-display-lg, 60px)',
                      }}
                    >
                      BlueGenAI
                    </span>
                </div>
                <Text variant={"display-xs"} weight="medium" className="font-bg-v2-display animate-initial-hidden animate-fade-in-up animate-delay-300" color="primary" align="center" as="p">
                  The fastest way to build applications
                </Text>
                <FormAlert
                  message={error}
                  variant="error"
                  onDismiss={handleDismissError}
                />
                <FormAlert
                  message={successMessage}
                  variant="success"
                  onDismiss={handleDismissSuccess}
                />
                <form className="flex flex-col w-full gap-bg-v2-2xl" onSubmit={handleSubmit} noValidate>
                  <Text variant={"text-lg"} color="secondary" className={"font-bg-v2-display"} weight={"semibold"} as="h2">
                    Sign in
                  </Text>
                      <Input
                        id="username"
                        name="username"
                        type="email"
                        label="Email"
                        placeholder="Enter your email address"
                        required
                        errorText={getEmailError()}
                        startIcon={<Icon name={IconName.Mail} size="md" />}
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          setEmailTouched(true);
                          setError('');
                          setSuccessMessage('');
                          sessionStorage.removeItem('loginError');
                        }}
                        onBlur={() => setEmailTouched(true)}
                      />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        label={
                          <div className="flex justify-between items-center w-full">
                            <span>Password</span>
                            <Link
                              to="/forgot-password"
                              className="text-bg-v2-sm font-bg-v2-medium text-bg-brand-solid hover:opacity-80 transition-smooth"
                            >
                              Forgot password?
                            </Link>
                          </div>
                        }
                        placeholder="Enter your password"
                        required
                        errorText={getPasswordError()}
                        startIcon={<Icon name={IconName.Lock} size="md" />}
                        endIcon={!getPasswordError() && <Icon name={showPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                        onEndIconClick={() => setShowPassword(!showPassword)}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordTouched(true);
                          setError('');
                          setSuccessMessage('');
                          sessionStorage.removeItem('loginError');
                        }}
                        onBlur={() => setPasswordTouched(true)}
                      />
                      <Checkbox
                        id="remember-me"
                        name="remember-me"
                        label="Remember me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                  <div ref={turnstileRef} />
                  <div>
                      <Button 
                        variant={"primary"}
                        type={"submit"}
                        loading={isLoading || !turnstileReady}
                        disabled={isLoading || hasFormErrors() || !turnstileReady || (turnstileAvailable && !turnstileToken)}
                        className={"w-full flex justify-center items-center hover-lift"} 
                        label={"Sign In"}
                      />
                  </div>
                  {SIGNUP_ENABLED && (
                    <div className="py-bg-v2-lg">
                      <Text variant={"text-sm"} color="secondary" align="center" className={"font-bg-v2-sans"} weight={"regular"} as="p">
                        Don't have an account?
                        <Link to="/signup" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 px-bg-v2-sm transition-smooth">Sign up</Link>
                      </Text>
                    </div>
                  )}
                </form>
            </Container>
          </Container>
        </div>
      </div>
    </div>

    {/* Update Password Modal */}
    <UpdatePasswordDialog
      isOpen={showUpdatePasswordModal}
      onClose={handleCloseModals}
      onSuccess={handlePasswordUpdateSuccess}
      userEmail={username}
    />

    {/* Update Profile Modal */}
    <UpdateProfileDialog
      isOpen={showUpdateProfileModal}
      onClose={handleCloseModals}
      onSuccess={handleProfileUpdateSuccess}
      userEmail={username}
      userPassword={password}
      actionData={actionData}
    />
   </>
  );
};